import PageHeader from '../headers/PageHeader';
import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import useScreenType from '../../hooks/utility/useScreenType';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';

interface Props {
  topContent?: ReactElement;
  leftColumn?: ReactElement;
  rightColumn?: ReactElement;
  children?: ReactElement;
  header?: ReactElement;
  footer?: ReactElement;
  hasHeader?: boolean;
  /**
   * When true, the desktop right column grows with its content (min-height, page scroll) instead
   * of being capped at the viewport (max-height, internal scroll). Used on the checkout screen so
   * the order summary is never clipped.
   */
  rightColumnGrow?: boolean;
}

const FixedPageLayout = (props: Props): ReactElement => {
  const { isDesktop } = useScreenType();

  if (!isDesktop) {
    return <FixedPageLayout.Mobile {...props} />;
  }

  return <FixedPageLayout.Desktop {...props} />;
};

export default FixedPageLayout;

FixedPageLayout.Desktop = ({ topContent, leftColumn, rightColumn, hasHeader = true, rightColumnGrow }: Props) => {
  const { isDesktop } = useScreenType();

  const rightColumnWidth = 400;
  const headerHeight = hasHeader ? 74 : 0;
  const topContentRef = useRef(null);
  const [stepperHeight, setStepperHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  const spacing = 24;

  const topOffset = Math.max(headerHeight + spacing, headerHeight + stepperHeight - scrollY);

  useLayoutEffect(() => {
    if (topContentRef.current) {
      setStepperHeight(topContentRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {hasHeader && (
        <div className="sticky w-screen top-0 z-50">
          <PageHeader />
        </div>
      )}
      {Boolean(topContent) && (
        <div ref={topContentRef} style={{ top: headerHeight }}>
          {topContent}
        </div>
      )}
      <div className="flex flex-row justify-center items-start w-full layout-container py-lg">
        <div className="w-full shrink px-lg lg:pr-[100px]">{leftColumn}</div>
        <div
          className={classnames({ sticky: !rightColumnGrow, 'self-start': rightColumnGrow })}
          style={{
            minWidth: rightColumnWidth,
            maxWidth: rightColumnWidth,
            top: rightColumnGrow ? undefined : topOffset,
          }}
        >
          <div
            className={classnames('border-xs flex flex-col', {
              'bg-brand-card dark:bg-black-600 rounded-lg': isDesktop,
              // Checkout: grow with content (page scrolls). Others: cap at viewport (inner scroll).
              'overflow-hidden': !rightColumnGrow,
            })}
            style={
              rightColumnGrow
                ? { minHeight: `calc(100vh - ${topOffset + spacing + 16}px)` }
                : { maxHeight: `calc(100vh - ${topOffset + spacing + 16}px)` }
            }
          >
            {rightColumn}
          </div>
        </div>
      </div>
    </>
  );
};

FixedPageLayout.Mobile = ({ children, header, topContent, footer }: Props) => {
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return (
      <div className="flex flex-col relative">
        {topContent}
        {children}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden">
      {header && <div className="shrink-0 z-50 bg-white shadow-section dark:shadow-none">{header}</div>}
      {topContent && <div className="shrink-0">{topContent}</div>}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-none">{children}</div>
      {footer && <div className="shrink-0 z-50">{footer}</div>}
    </div>
  );
};
